import React, { useEffect, useRef, useContext, useCallback } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import useMediaQuery from '../../utils/useMediaQuery';
import isEmpty from '../../utils/isEmpty';

import VisibilitySensor from 'react-visibility-sensor';

import BackgroundImage from '../BackgroundImage';
import ImageCaption from '../ImageCaption';

import Modal from '../Modal';

import ImageGalleryPager from './Components/ImageGalleryPager';
import ImageGalleryThumbnail from './Components/ImageGalleryThumbnail';
import ImageGalleryResponsiveFullscreenImage from './Components/ImageGalleryResponsiveFullscreenImage';
import ImageDescription from './Components/ImageDescription';

import ImageGalleryFullscreenCaption from './Components/ImageGalleryFullscreenCaption';
import ImageGalleryFullscreenButton from './Components/ImageGalleryFullscreenButton';

import { serializeImage } from '../../utils/SerializeImage';
import { breakpoints } from '../../constants';
import { useLocalStateContext, LocalStateContext } from './state';
import { useTranslation } from 'next-i18next';

import styles from './ImageGallery.module.scss';
import AnchorLink from '../AnchorLink';

const ImageGallery = ({ heading = null, anchorLink = '', images = [] }) => {
    const serializedImages = images.map((item, i) => ({
        ...serializeImage(item.image, 'true'),
        id: item.image.id,
        index: i + 1,
    }));

    const context = useLocalStateContext(serializedImages);
    const { state, handleFullscreenToggle } = context;

    const isMobile = useMediaQuery({
        query: `(max-width: ${breakpoints.sm}px)`,
    });

    if (isEmpty(serializedImages)) {
        return null;
    }

    if (serializedImages.length === 1) {
        return <SingleImageInlineGallery heading={heading} anchorLink={anchorLink} image={serializedImages[0]} />;
    }

    return (
        <LocalStateContext.Provider value={context}>
            <InlineGallery heading={heading} anchorLink={anchorLink} images={serializedImages} />
            <Modal open={state.isFullscreen && !isMobile} handleClose={handleFullscreenToggle}>
                <FullscreenImageGallery images={serializedImages} />
            </Modal>
        </LocalStateContext.Provider>
    );
};

ImageGallery.propTypes = {
    heading: PropTypes.string,
    anchorLink: PropTypes.string,
    images: PropTypes.array,
};

const SingleImageInlineGallery = ({ heading, anchorLink, image }) => (
    <div className={styles['ImageGallery']}>
        <div className={styles['ImageGallery__Container']}>
            {heading && (
                <h2 className={styles['ImageGallery__Heading']}>
                    <AnchorLink title={heading} id={anchorLink} />
                </h2>
            )}
            <div className={styles['ImageGallery__Wrapper'] + ' ' + styles['ImageGallery__Wrapper--SingleImage']}>
                <GalleryImage image={image} />
                <ImageCaption
                    caption={
                        <ImageDescription
                            id={`image-${image.id}`}
                            title={image.title}
                            caption={image.caption}
                            credits={image.credits}
                        />
                    }
                />
            </div>
        </div>
    </div>
);

SingleImageInlineGallery.propTypes = {
    heading: PropTypes.string,
    anchorLink: PropTypes.string,
    image: PropTypes.object,
};

const InlineGallery = ({ heading = null, anchorLink = null, images }) => {
    const { state, ...handlers } = useContext(LocalStateContext);

    const isDesktop = useMediaQuery({
        query: `(min-width: ${breakpoints.l}px)`,
    });

    const imagesToRender = !isDesktop ? images : images.slice(0, 6);
    const imagesLeft = images.length - imagesToRender.length;

    return (
        <div className={styles['ImageGallery']}>
            <div className={styles['ImageGallery__Container']}>
                {heading && (
                    <h2 className={styles['ImageGallery__Heading']}>
                        <AnchorLink title={heading} id={anchorLink} />
                    </h2>
                )}

                <div className={styles['ImageGallery__Wrapper']}>
                    <ImageGalleryPager
                        untranslatedModifiers={['ImageGalleryPager--Desktop-only']}
                        current={state.selectedImageIndex}
                        total={images.length}
                    />
                    <ImageGalleryFullscreenButton onClick={handlers.handleFullscreenToggle} />

                    <GalleryImage image={state.selectedImage} onClick={handlers.handleFullscreenToggle} />

                    <ImageGalleryArrows images={images} />
                    <ImageCaption
                        caption={
                            <ImageDescription
                                title={state.selectedImage.title}
                                caption={state.selectedImage.caption}
                                credits={state.selectedImage.credits}
                            />
                        }
                    />
                </div>

                <div className={styles['ImageGallery__Thumbnails']}>
                    <ImageGalleryPager
                        current={state.displayMultiThumbIndex}
                        total={images.length}
                        untranslatedModifiers={['ImageGalleryPager--Responsive-only']}
                    />
                    {imagesToRender.map((image) => (
                        <VisibilitySensor
                            key={image.id}
                            delayedCall={true}
                            scrollCheck={true}
                            scrollDelay={30}
                            onChange={(visible) => handlers.handleImageInViewChange(visible, image)}>
                            <ImageGalleryThumbnail
                                image={image}
                                onImageSelect={handlers.handleImageSelect}
                                onFullscreenToggle={handlers.handleFullscreenToggle}
                            />
                        </VisibilitySensor>
                    ))}
                    {isDesktop && imagesLeft > 0 && (
                        <div className={styles['ImageGallery__Thumbnails__MoreAvailable']}>
                            +{imagesLeft}
                            <span className="sr-only">{imagesLeft} available</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

InlineGallery.propTypes = {
    heading: PropTypes.string,
    anchorLink: PropTypes.string,
    images: PropTypes.array.isRequired,
};

const FullscreenImageGallery = ({ images }) => {
    const thumbRefs = useRef([]);
    const { state, ...handlers } = useContext(LocalStateContext);

    const isDesktop = useMediaQuery({
        query: `(min-width: ${breakpoints.l}px)`,
    });

    const sizes = '(min-width: 1280px) 65vw, 100vw';

    const thumbContainerClasses = classNames(styles['ImageGallery__Thumbnails'], {
        [styles['ImageGallery__Thumbnails--Fullscreen']]: !isDesktop,
    });

    useEffect(() => {
        const timeOut = isDesktop ? 0 : 500;

        setTimeout(() => {
            const selectedThumbRef = thumbRefs.current[state.selectedImage.index - 1];

            if (selectedThumbRef) {
                selectedThumbRef.scrollThumbIntoView();
            }
        }, timeOut);
    }, [isDesktop, state.selectedImageIndex, state.selectedImage.index]);

    return (
        <div className={styles['ImageGallery'] + ' ' + styles['ImageGallery--Fullscreen']}>
            <div className={styles['ImageGallery__Container'] + ' ' + styles['ImageGallery__Container--Fullscreen']}>
                <div className={styles['ImageGallery__Wrapper'] + ' ' + styles['ImageGallery__Wrapper--Fullscreen']}>
                    <ImageGalleryPager
                        modifiers="ImageGalleryPager--Desktop-only"
                        current={state.selectedImageIndex}
                        total={images.length}
                    />

                    <GalleryImage image={state.selectedImage} sizes={sizes} />

                    <ImageCaption
                        caption={
                            <ImageDescription
                                title={state.selectedImage.title}
                                caption={state.selectedImage.caption}
                                credits={state.selectedImage.credits}
                            />
                        }
                    />
                    {isDesktop && <ImageGalleryArrows images={images} />}
                </div>

                {!isDesktop && (
                    <div className={styles['ImageGallery__ThumbnailsWrapper']}>
                        <div className={thumbContainerClasses}>
                            {images.map((image, i) => (
                                <VisibilitySensor
                                    active={!isDesktop}
                                    key={image.id}
                                    scrollCheck={true}
                                    scrollDelay={30}
                                    onChange={(visible) => handlers.handleImageInViewChange(visible, image)}>
                                    <ImageGalleryResponsiveFullscreenImage
                                        image={image}
                                        ref={(el) => (thumbRefs.current[i] = el)}>
                                        <ImageGalleryFullscreenCaption
                                            image={image}
                                            isActive={state.displayThumbIndex === image.index}
                                            totalImageCount={images.length}
                                        />
                                    </ImageGalleryResponsiveFullscreenImage>
                                </VisibilitySensor>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {isDesktop && (
                <div
                    className={
                        styles['ImageGallery__ThumbnailsWrapper'] +
                        ' ' +
                        styles['ImageGallery__ThumbnailsWrapper--Fullscreen']
                    }>
                    <div className={thumbContainerClasses}>
                        {images.map((image, i) => (
                            <VisibilitySensor
                                active={!isDesktop}
                                key={image.id}
                                scrollCheck={true}
                                scrollDelay={30}
                                onChange={(visible) => handlers.handleImageInViewChange(visible, image)}>
                                <ImageGalleryThumbnail
                                    ref={(el) => (thumbRefs.current[i] = el)}
                                    image={image}
                                    onImageSelect={handlers.handleImageSelect}
                                    active={state.selectedImageIndex === image.index}
                                />
                            </VisibilitySensor>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

FullscreenImageGallery.propTypes = {
    images: PropTypes.array.isRequired,
};

const GalleryImage = ({ image = {}, sizes = '', onClick = null }) => {
    const imageClasses = classNames(styles['ImageGallery__Image'], {
        // [styles['ImageGallery__Image--Portrait']]: image.portrait,
        [styles['ImageGallery__Image--Clickable']]: onClick !== null,
    });

    const WrapperElement = onClick ? 'button' : 'div';

    // For content full width, used for single image or first large image in desktop
    const defaultSizes =
        '(min-width: 1440px) 1046px, (min-width: 1280px) 926px, (min-width: 1024px) 920px, (min-width: 768px) 720px, 100vw';

    return (
        <WrapperElement onClick={onClick} className={imageClasses}>
            <BackgroundImage {...image} sizes={sizes || defaultSizes} />
        </WrapperElement>
    );
};

GalleryImage.propTypes = {
    image: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        index: PropTypes.number.isRequired,
        src: PropTypes.string.isRequired,
        srcSet: PropTypes.string,
        alt: PropTypes.string,
        title: PropTypes.string,
        focal: PropTypes.object,
    }),
    onClick: PropTypes.func,
};

const ImageGalleryArrows = ({ images = {} }) => {
    const { state, ...handlers } = useContext(LocalStateContext);

    const selectPrevImage = useCallback(
        (e) => {
            const prevImgIndex = state.selectedImageIndex - 2;

            if (!(prevImgIndex in images)) {
                handlers.handleImageSelect(e, images[images.length - 1]);
                return;
            }

            handlers.handleImageSelect(e, images[prevImgIndex]);
        },
        [state, images, handlers]
    );

    const selectNextImage = useCallback(
        (e) => {
            const nextImgIndex = state.selectedImageIndex;

            if (!(nextImgIndex in images)) {
                handlers.handleImageSelect(e, images[0]);
                return;
            }

            handlers.handleImageSelect(e, images[nextImgIndex]);
        },
        [state, images, handlers]
    );

    const handleArrowKeyPress = useCallback(
        (e) => {
            const leftArrowCode = 37;
            const rightArrowCode = 39;

            if (e.keyCode === leftArrowCode) {
                selectPrevImage(e);
            }

            if (e.keyCode === rightArrowCode) {
                selectNextImage(e);
            }
        },
        [selectPrevImage, selectNextImage]
    );

    useEffect(() => {
        if (state.isFullscreen) {
            document.addEventListener('keydown', handleArrowKeyPress, false);
        }

        return () => {
            document.removeEventListener('keydown', handleArrowKeyPress, false);
        };
    }, [handleArrowKeyPress, state.isFullscreen]);

    return (
        <>
            <span>
                <button className={styles['ImageGallery__ArrowLeft']} onClick={selectPrevImage}>
                    <span className="sr-only">Previous image</span>
                </button>
            </span>

            <span>
                <button className={styles['ImageGallery__ArrowRight']} onClick={selectNextImage}>
                    <span className="sr-only">Next image</span>
                </button>
            </span>
        </>
    );
};

ImageGalleryArrows.propTypes = {
    images: PropTypes.array,
};

export default ImageGallery;
